import { headers, tableData } from "../constant/Home/Dashboard";
import Button from "@mui/material/Button";

// Local helper to humanize enum-like strings (e.g., EXTERIOR_STEPS_REPAIR -> "Exterior Steps Repair")
const toTitleFromEnum = (value) => {
  if (!value || typeof value !== "string") return "";
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};
export const isWarrantyExpired = (row) => {
  try {
    const js = row?.jobSnapshot || row || {};
    const completionStr =
      js?.dateOfCompletion ||
      js?.date_of_completion ||
      js?.dateCompleted ||
      js?.date_completed ||
      js?.completedAt ||
      js?.completed_at;

    if (!completionStr) return false; // No completion date → allow warranty flow

    const completed = new Date(completionStr);
    if (isNaN(completed.getTime())) return false;

    const now = new Date();
    const diffMs = now.getTime() - completed.getTime();
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    return diffMs > ninetyDaysMs;
  } catch (e) {
    return false;
  }
};

export const getHeaders = (accountType, tableType) => {
  const headerConfig = headers[tableType];

  if (Array.isArray(headerConfig)) {
    // Filter headers based on user type and table type
    if (tableType === "openInvoices") {
      if (accountType === "VENDOR") {
        // For VENDOR: don't show PAY NOW, VENDOR, WARRANTY SERVICE, DATE PAID columns
        return headerConfig.filter(
          (header) =>
            header !== "PAY NOW" &&
            header !== "VENDOR" &&
            header !== "WARRANTY SERVICE" &&
            header !== "DATE PAID"
        );
      } else if (["HOMEOWNER", "STR_HOST", "LANDLORD", "PROPERTY_MANAGER", "FAMILY_MEMBER"].includes(accountType)) {
        // For HOMEOWNER/LANDLORD/FAMILY_MEMBER: don't show JOB DESCRIPTION column
        return headerConfig.filter((header) => header !== "JOB DESCRIPTION");
      }

      return headerConfig;
    } else if (tableType === "paidInvoices") {
      if (accountType === "VENDOR") {
        // For VENDOR: don't show VENDOR, JOB TITLE, WARRANTY SERVICE columns
        return headerConfig.filter(
          (header) =>
            header !== "VENDOR" &&
            header !== "JOB TITLE" &&
            header !== "WARRANTY SERVICE"
        );
      } else if (["HOMEOWNER", "STR_HOST", "LANDLORD", "PROPERTY_MANAGER", "FAMILY_MEMBER"].includes(accountType)) {
        // For HOMEOWNER/LANDLORD/FAMILY_MEMBER: don't show JOB DESCRIPTION column
        return headerConfig.filter((header) => header !== "JOB DESCRIPTION");
      }
    }

    // Return all headers for other cases
    return headerConfig;
  }

  return headerConfig;
};

export const getTableData = (accountType, tableType) => {
  const dataConfig = tableData[tableType];

  if (Array.isArray(dataConfig)) {
    // Return the same data for all user types since we now have all fields
    return dataConfig;
  }

  return dataConfig;
};

export const renderCell = (row, header, colIdx, options = {}) => {
  const { title, setModalState, setCurrentSelectedData, handleApproveRequest } =
    options;

  switch (header) {
    case "Date Requested":
      return formatDate(row?.createdAt);
    case "JOB NUMBER":
      return row?.jobNumber;
    case "PROPERTY ADDRESS":
      return row?.propertyAddress?.streetAddress;
    case "JOB TITLE": {
      // Prefer the display label for the sub-issue when available
      const title =
        row?.subIssueTypeDisplay ||
        row?.sub_issue_type_display ||
        (row?.subIssueType ? toTitleFromEnum(row?.subIssueType) : null) ||
        (row?.issueType ? toTitleFromEnum(row?.issueType) : null);
      return title || "N/A";
    }
    // return row?.issueType + row?.subIssueType;
    case "DATE ISSUED":
      return row.dateIssued;
    case "INVOICE ID":
      return row.invoiceId;
    case "TIME ISSUED": {
      // Show only the local time. Prefer createdAt as the source; fall back to any provided timeIssued string.
      const source = row?.createdAt || row?.created_at || row?.timeIssued;
      return formatTime(source);
    }
    case "LOCATION":
      return row.location;
    case "ESTIMATED RANGE":
      return row.estimatedRange;
    case "AMOUNT":
      return formatCurrency(row.amount);
    case "VENDOR":
      return row.vendor || "N/A";
    case "JOB DESCRIPTION":
      return row.jobDescription || "N/A";
    case "DATE PAID":
      return row.datePaid || "N/A";
    case "VIEW DETAILS":
      return (
        <Button
          variant="outlined"
          size="small"
          style={{
            borderRadius: 8,
            textTransform: "none",
            border: "1px solid #ECECEC",
            color: "#242424",
          }}
          onClick={() => {
            setModalState("jobDetails");
            setCurrentSelectedData(row);
          }}
        >
          View
        </Button>
      );
    case "VIEW DETAILS TO ACCEPT":
      return (
        <Button
          variant="outlined"
          size="small"
          style={{
            borderRadius: 8,
            textTransform: "none",
            border: "1px solid #ECECEC",
            color: "#242424",
          }}
          onClick={() => {
            setModalState("jobDetails");
          }}
        >
          View
        </Button>
      );
    case "APPROVE":
      return (
        <Button
          variant="contained"
          size="small"
          style={{
            borderRadius: 8,
            textTransform: "none",
            backgroundColor: "#F2FBF4",
            color: "#3CAB5C",
            border: "1px solid #3CAB5C",
          }}
          onClick={() => handleApproveRequest(row.id)}
        >
          Approve
        </Button>
      );
    case "PAY NOW":
      return (
        <Button
          variant="contained"
          size="small"
          style={{
            borderRadius: 100,
            textTransform: "none",
            backgroundColor: "#F2FBF4",
            color: "#3CAB5C",
            border: "1px solid #3CAB5C",
            minWidth: "77px",
            textWrap: "nowrap",
          }}
        >
          Pay Now
        </Button>
      );
    case "VIEW":
      return (
        <Button
          variant="outlined"
          size="small"
          style={{
            borderRadius: 8,
            textTransform: "none",
            border: "1px solid #ECECEC",
            color: "#242424",
          }}
        >
          View
        </Button>
      );
    case "DOWNLOAD":
      return (
        <Button
          variant="outlined"
          size="small"
          style={{
            borderRadius: 8,
            textTransform: "none",
            border: "1px solid #ECECEC",
            color: "#242424",
          }}
        >
          Download
        </Button>
      );
    default:
      return row[header.toLowerCase()] || "N/A";
  }
};

availableJobs: [
  "DATE ISSUED",
  "TIME ISSUED",
  "ESTIMATED RANGE",
  "LOCATION",
  "JOB TITLE",
  "JOB DESCRIPTION",
  "VIEW DETAILS TO ACCEPT",
];

// utils/dateUtils.ts
export const formatDate = (isoDate) => {
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date format");
    }
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      // hour: "numeric",
      // minute: "2-digit",
      // second: "2-digit",
      // hour12: true,
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid Date";
  }
};

// Formats just the time component in the user's locale (e.g., 8:18 AM)
export const formatTime = (isoOrTimeLike) => {
  try {
    if (!isoOrTimeLike) return "";

    // 1) If value is parsable as a Date, show local time only
    const date = new Date(isoOrTimeLike);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    // 2) If not a valid Date, attempt to extract a time portion from a string
    if (typeof isoOrTimeLike === "string") {
      // Match formats like "2:05 PM" or "12:30 am"
      const ampmMatch = isoOrTimeLike.match(/\b(\d{1,2}:\d{2})\s*([AaPp][Mm])\b/);
      if (ampmMatch) return `${ampmMatch[1]} ${ampmMatch[2].toUpperCase()}`;

      // Match 24h time like "14:05" or "9:07"
      const hmMatch = isoOrTimeLike.match(/\b(\d{1,2}:\d{2})\b/);
      if (hmMatch) return hmMatch[1];
    }

    // 3) Fallback to raw string
    return String(isoOrTimeLike);
  } catch (e) {
    return String(isoOrTimeLike ?? "");
  }
};

// Converts enum-like strings (e.g., PROPERTY_GROUNDS) to human readable ("Property Grounds")
export const humanizeEnum = (value) => {
  if (!value || typeof value !== "string") return "";
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

/**
 * Formats a scheduled time string like "2026-02-03|allDay" into 
 * a human-friendly format: "2/3/2026 (All Day)".
 */
export const formatScheduledTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return timeStr || "";

  if (timeStr.includes("|")) {
    const [datePart, slotPart] = timeStr.split("|");
    let formattedDate = datePart;

    const parts = datePart.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      formattedDate = `${parseInt(month)}/${parseInt(day)}/${year}`;
    }

    const slotMap = {
      allday: "All Day",
      morning: "Morning 8am - 12pm",
      afternoon: "Afternoon 12pm - 4pm",
      evening: "Evening 4pm - 8pm",
    };

    const slotKey = (slotPart || "").toLowerCase().replace(/[^a-z]/g, "");
    const displaySlot = slotMap[slotKey] || humanizeEnum(slotPart);

    return `${formattedDate} (${displaySlot})`;
  }

  return timeStr;
};

export const formatCurrency = (val) => {
  if (val === null || val === undefined) return "";
  let numericVal;
  if (typeof val === "number") {
    numericVal = val;
  } else {
    const str = String(val);
    const sanitized = str.replace(/[^\d.-]/g, "");
    numericVal = parseFloat(sanitized);
  }

  if (Number.isNaN(numericVal)) return String(val);

  const isNegative = numericVal < 0;
  const withCommas = Math.abs(numericVal).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${isNegative ? "-" : ""}$${withCommas}`;
};

// Formats a combined issue and sub-issue label, e.g., humanizeEnum(issue) + " (" + humanizeEnum(sub) + ")"
export const formatIssueAndSubIssue = (issue, subIssue) => {
  const base = humanizeEnum(issue);
  const sub = humanizeEnum(subIssue);
  return sub ? `${base} (${sub})` : base;
};

// Resolve an estimated range from multiple possible shapes/keys
export const resolveEstimatedRange = (row) => {
  const fromPaths = (obj, paths) => {
    for (const p of paths) {
      const parts = p.split(".");
      let cur = obj;
      let ok = true;
      for (const key of parts) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, key)) {
          cur = cur[key];
        } else {
          ok = false;
          break;
        }
      }
      if (ok && cur != null && cur !== "") return cur;
    }
    return null;
  };

  // Direct combined range string/number
  const direct = fromPaths(row, [
    "estimatedRange",
    "estimated_range",
    "priceEstimate",
    "price_estimate",
    "budgetRange",
    "budget_range",
    "jobSnapshot.estimatedRange",
    "jobSnapshot.priceEstimate",
    "jobSnapshot.price_estimate",
    "requestSnapshot.estimatedRange",
    "requestSnapshot.priceEstimate",
    "requestSnapshot.price_estimate",
  ]);

  // If it's an array like [min, max]
  if (Array.isArray(direct) && direct.length >= 2) {
    const [min, max] = direct;
    if (min != null && max != null) {
      return `${formatCurrency(min)} - ${formatCurrency(max)}`;
    }
  }

  // If direct is a string like "$200-300" or "200-300"
  if (typeof direct === "string") {
    const rangeMatch = direct.match(
      /(-?\$?\s*\d[\d,]*)\s*[-–]\s*(\$?\s*\d[\d,]*)/
    );
    if (rangeMatch) {
      const minStr = formatCurrency(rangeMatch[1]);
      const maxStr = formatCurrency(rangeMatch[2]);
      return `${minStr} - ${maxStr}`;
    }
    // Single numeric-like string
    if (/\d/.test(direct)) return formatCurrency(direct);
    return direct;
  }

  if (typeof direct === "number") {
    return formatCurrency(direct);
  }

  // Try min/max field combos
  const candidates = [
    ["minEstimate", "maxEstimate"],
    ["estimateMin", "estimateMax"],
    ["min_estimate", "max_estimate"],
    ["minBudget", "maxBudget"],
    ["budgetMin", "budgetMax"],
    ["min_budget", "max_budget"],
    ["priceMin", "priceMax"],
    ["price_min", "price_max"],
  ];

  const sources = [row, row?.jobSnapshot, row?.requestSnapshot];
  for (const src of sources) {
    if (!src) continue;
    for (const [minKey, maxKey] of candidates) {
      const minVal = src[minKey];
      const maxVal = src[maxKey];
      if (minVal != null && maxVal != null) {
        return `${formatCurrency(minVal)} - ${formatCurrency(maxVal)}`;
      }
      if (minVal != null) return formatCurrency(minVal);
      if (maxVal != null) return formatCurrency(maxVal);
    }
  }

  return null;
};
